/**
 * Help › About kn.engine: the engine's name and version, who built it, and the
 * credits and notices for what it shows (third-party assets, other studios'
 * IP). The credits and notices are content (the seed's `about`, rule 6); the
 * dialog only lays them out, and leaves out what the content doesn't have.
 */
import { useEffect, useRef } from "react";
import { useEditor } from "../EditorContext";
import type { SeedCredit } from "../../assets/content";
import { useCloseOnEscape } from "../ui/useCloseOnEscape";
import "../ui/modal.css";
import "./about.css";

export function AboutDialog({ onClose }: { onClose: () => void }) {
  const { content, links } = useEditor();
  const { profile, about } = content.seed;
  const closeRef = useRef<HTMLButtonElement>(null);
  useCloseOnEscape(onClose);

  // Focus moves into the dialog on open (WAI-ARIA dialog pattern).
  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  return (
    <div
      className="kn-modal__scrim"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="kn-modal kn-about"
        role="dialog"
        aria-modal="true"
        aria-labelledby="kn-about-title"
      >
        <header className="kn-modal__header">
          <h2 id="kn-about-title">About kn.engine</h2>
          <button ref={closeRef} type="button" className="kn-modal__close" onClick={onClose}>
            Close
          </button>
        </header>
        <div className="kn-about__body">
          <p className="kn-about__engine">
            <span className="kn-about__mark" aria-hidden="true" />
            kn.engine {__KN_VERSION__}
          </p>
          <p>Built by {profile.name}.</p>
          <p className="kn-about__links">
            {links.github && (
              <a href={links.github} target="_blank" rel="noopener">
                GitHub
              </a>
            )}
            {links.linkedin && (
              <a href={links.linkedin} target="_blank" rel="noopener">
                LinkedIn
              </a>
            )}
            <a href={links.resume} download>
              Resume.pdf
            </a>
          </p>

          {about && about.credits.length > 0 && (
            <section aria-labelledby="kn-about-credits">
              <h3 id="kn-about-credits">Credits</h3>
              <ul className="kn-about__credits" aria-label="Credits">
                {about.credits.map((credit) => (
                  <li key={`${credit.subject}:${credit.url}`}>
                    <CreditLine credit={credit} />
                  </li>
                ))}
              </ul>
            </section>
          )}

          {about && about.notices.length > 0 && (
            <section aria-labelledby="kn-about-notices">
              <h3 id="kn-about-notices">Notices</h3>
              {about.notices.map((notice) => (
                <p key={notice} className="kn-about__notice">
                  {notice}
                </p>
              ))}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * What a Creative Commons credit asks for: the subject, the work's title, its
 * author (linked to the work), where it's from, and the license (linked).
 */
function CreditLine({ credit }: { credit: SeedCredit }) {
  return (
    <>
      <span className="kn-about__subject">{credit.subject}:</span>{" "}
      {credit.title && <>&ldquo;{credit.title}&rdquo; by </>}
      <a href={credit.url} target="_blank" rel="noopener">
        {credit.author}
      </a>
      {credit.source && <>, on {credit.source}</>}.{" "}
      {credit.licenseUrl ? (
        <a href={credit.licenseUrl} target="_blank" rel="noopener">
          {credit.license}
        </a>
      ) : (
        credit.license
      )}
      .
    </>
  );
}
